import { Bell, BellOff, Camera, Flag, Heart, Star, X } from 'lucide-react'
import { useState } from 'react'
import { Link, useLocation } from 'react-router'
import { toast } from 'sonner'
import { useAction } from '@/api/driver'
import { isMissing, reviews, support, useStationReviews, type Review } from '@/api/proposed'
import type { MapCharger, MapStation } from '@/api/types'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Field, Select, Textarea } from '@/components/ui/form'
import { Dialog } from '@/components/ui/overlay'
import { useAuth } from '@/lib/auth'
import { useFavourites } from '@/lib/favourites'
import { date, time } from '@/lib/format'
import { useI18n } from '@/lib/i18n'
import { cn, errorText } from '@/lib/utils'
import { freeMatching, useWatches } from '@/lib/watch'

export function FavouriteButton({ stationId, className }: { stationId: number; className?: string }) {
  const { t } = useI18n()
  const { ids, toggle } = useFavourites()
  const on = ids.has(stationId)
  return (
    <button
      aria-pressed={on}
      aria-label={on ? t('Remove from favourites') : t('Add to favourites')}
      onClick={async (e) => {
        e.stopPropagation()
        const now = await toggle(stationId)
        toast.success(now ? t('Saved to favourites') : t('Removed from favourites'))
      }}
      className={cn('grid size-9 place-items-center rounded-lg hover:bg-surface-3', className)}
    >
      <Heart className={cn('size-5', on ? 'fill-danger text-danger' : 'text-text-2')} />
    </button>
  )
}

/** "Tell me when a connector is free" for a busy station. */
export function WatchCard({ station }: { station: MapStation }) {
  const { t } = useI18n()
  const w = useWatches()
  const types = [...new Set(station.chargers.flatMap((c) => c.connectors.map((k) => k.connector_type)))]
  const [type, setType] = useState<string>('')
  const active = w.get(station.id)
  const freeNow = freeMatching(station, type || null).length
  if (active)
    return (
      <Card className="flex items-center gap-3 p-4">
        <Bell className="size-5 shrink-0 text-brand-ink dark:text-brand" />
        <div className="min-w-0 flex-1 text-sm">
          <p className="font-medium">{t('We will tell you when {type} is free', { type: active.connector_type ?? t('a connector') })}</p>
          <p className="text-xs text-text-2">{t('Until {time}. Keep this site open in a tab, with notifications allowed.', { time: time(new Date(active.until)) })}</p>
        </div>
        <Button size="sm" variant="ghost" onClick={() => w.remove(station.id)}><BellOff /> {t('Stop')}</Button>
      </Card>
    )
  return (
    <Card className="p-4">
      <p className="font-medium">{t('All busy? Get told when one frees up')}</p>
      <div className="mt-3 flex flex-wrap gap-2">
        <Select className="w-44" value={type} onChange={(e) => setType(e.target.value)} aria-label={t('Connector type')}>
          <option value="">{t('Any connector')}</option>
          {types.map((x) => <option key={x} value={x}>{x}</option>)}
        </Select>
        <Button
          variant="outline"
          disabled={freeNow > 0}
          onClick={async () => {
            await w.add(station, type || null)
            toast.success(t('Watching {station}', { station: station.name }))
          }}
        >
          <Bell /> {freeNow > 0 ? t('{n} free now', { n: freeNow }) : t('Notify me')}
        </Button>
      </div>
    </Card>
  )
}

const ISSUES = ['Will not start', 'Stopped early', 'Error on the screen', 'Cable or connector damaged', 'Wrong price or charge', 'Bay blocked by another car', 'Something else']

export function ReportProblemButton({ station, charger }: { station: MapStation; charger?: MapCharger }) {
  const { t } = useI18n()
  const [open, setOpen] = useState(false)
  return (
    <>
      <button onClick={() => setOpen(true)} className="inline-flex items-center gap-1.5 text-[13px] text-text-2 hover:text-text">
        <Flag className="size-3.5" /> {t('Report a problem')}
      </button>
      {open && <ReportProblemDialog station={station} charger={charger} onClose={() => setOpen(false)} />}
    </>
  )
}

function PhotoPicker({ files, onChange, max = 3 }: { files: File[]; onChange: (f: File[]) => void; max?: number }) {
  const { t } = useI18n()
  return (
    <div className="flex flex-wrap gap-2">
      {files.map((f, i) => (
        <div key={i} className="relative">
          <img src={URL.createObjectURL(f)} alt="" className="size-16 rounded-lg object-cover" />
          <button aria-label={t('Remove photo')} onClick={() => onChange(files.filter((_, k) => k !== i))} className="absolute -right-1.5 -top-1.5 grid size-5 place-items-center rounded-full bg-ink text-white"><X className="size-3" /></button>
        </div>
      ))}
      {files.length < max && (
        <label className="grid size-16 cursor-pointer place-items-center rounded-lg border border-dashed border-border text-text-2 hover:bg-surface-2">
          <Camera className="size-5" />
          <span className="sr-only">{t('Add photo')}</span>
          <input type="file" accept="image/*" capture="environment" hidden onChange={(e) => { const f = e.target.files?.[0]; if (f) onChange([...files, f]); e.target.value = '' }} />
        </label>
      )}
    </div>
  )
}

function ReportProblemDialog({ station, charger, onClose }: { station: MapStation; charger?: MapCharger; onClose: () => void }) {
  const { t } = useI18n()
  const { user } = useAuth()
  const { pathname } = useLocation()
  const [chargerId, setChargerId] = useState(charger?.id ?? station.chargers[0]?.id ?? '')
  const [connector, setConnector] = useState<number | ''>('')
  const [issue, setIssue] = useState('')
  const [text, setText] = useState('')
  const [photos, setPhotos] = useState<File[]>([])
  const [fallback, setFallback] = useState(false)
  const ch = station.chargers.find((c) => c.id === chargerId)
  const phone = station.contacts[0]?.phone
  const summary = `${issue} · ${station.name} · ${chargerId}${connector ? ` #${connector}` : ''}`
  const send = useAction(
    () => support.create({ kind: 'problem', subject: `${issue}: ${chargerId}${connector ? ` #${connector}` : ''}`, message: text.trim() || issue, station_id: station.id, charger_id: chargerId, connector_number: connector || undefined, photos }),
    {
      quietError: true,
      success: t('Thanks. The station team has your report and will reply in Help.'),
      onSuccess: onClose,
    },
  )
  const submit = () =>
    send.mutate(undefined, {
      onError: (e) => {
        if (isMissing(e)) setFallback(true)
        else toast.error(errorText(e))
      },
    })

  const contact = (
    <div className="space-y-2 text-sm">
      {phone && <a href={`tel:${phone}`} className="flex h-11 items-center justify-center rounded-lg bg-primary font-medium text-primary-fg">{t('Call the station')} · {phone}</a>}
      <a href={`mailto:summit@saadhan.app?subject=${encodeURIComponent(`Problem: ${summary}`)}&body=${encodeURIComponent(text)}`} className="flex h-11 items-center justify-center rounded-lg border border-border font-medium">{t('Email Saadhan Charge')}</a>
    </div>
  )

  return (
    <Dialog
      open
      onOpenChange={(o) => !o && onClose()}
      title={t('Report a problem')}
      description={station.name}
      footer={!fallback && user ? <><Button variant="outline" onClick={onClose}>{t('Cancel')}</Button><Button loading={send.isPending} disabled={!issue} onClick={submit}>{t('Send report')}</Button></> : undefined}
    >
      {fallback ? (
        <div className="space-y-3">
          <p className="text-sm text-text-2">{t('Reports from the website are not switched on yet. Please tell the station directly:')}</p>
          {contact}
        </div>
      ) : (
        <div className="grid gap-4">
          <div className="flex flex-wrap gap-2" role="radiogroup" aria-label={t('What is wrong?')}>
            {ISSUES.map((x) => (
              <button key={x} role="radio" aria-checked={issue === x} onClick={() => setIssue(x)} className={cn('rounded-full border px-3 py-1.5 text-[13px]', issue === x ? 'border-ink bg-ink text-white dark:border-brand dark:bg-brand dark:text-ink' : 'border-border hover:bg-surface-2')}>{t(x)}</button>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label={t('Charger')}>{(p) => <Select {...p} value={chargerId} onChange={(e) => { setChargerId(e.target.value); setConnector('') }}>{station.chargers.map((c) => <option key={c.id} value={c.id}>{c.id}</option>)}</Select>}</Field>
            <Field label={t('Connector')}>{(p) => <Select {...p} value={connector} onChange={(e) => setConnector(e.target.value ? Number(e.target.value) : '')}><option value="">{t('Not sure')}</option>{ch?.connectors.map((k) => <option key={k.connector_number} value={k.connector_number}>#{k.connector_number} {k.connector_type}</option>)}</Select>}</Field>
          </div>
          <Field label={t('Details (optional)')}>{(p) => <Textarea {...p} value={text} onChange={(e) => setText(e.target.value)} placeholder={t('Error code, what you tried…')} />}</Field>
          <div>
            <p className="mb-2 text-[13px] font-medium">{t('Photos (optional)')}</p>
            <PhotoPicker files={photos} onChange={setPhotos} />
          </div>
          {!user && (
            <div className="rounded-lg bg-surface-2 p-3 text-sm">
              <p><Link to={`/login?next=${encodeURIComponent(pathname)}`} className="font-medium underline">{t('Sign in')}</Link> {t('to send a report and get a reply, or contact the station now:')}</p>
              <div className="mt-3">{contact}</div>
            </div>
          )}
        </div>
      )}
    </Dialog>
  )
}

export function Stars({ value, size = 'size-4' }: { value: number; size?: string }) {
  return (
    <span className="inline-flex" aria-label={`${value.toFixed(1)} / 5`}>
      {[1, 2, 3, 4, 5].map((i) => <Star key={i} className={cn(size, i <= Math.floor(value + 0.25) ? 'fill-brand text-brand' : 'text-border-strong')} />)}
    </span>
  )
}

/** Reviews on the station page. Hidden entirely until the API has them (D11). */
export function StationReviews({ station }: { station: MapStation }) {
  const { t } = useI18n()
  const { user } = useAuth()
  const q = useStationReviews(station.id)
  const [writing, setWriting] = useState(false)
  if (q.isLoading || q.error || !q.data) return null
  const { average, count, reviews: list } = q.data
  const photos = list.flatMap((r) => r.photo_urls)
  return (
    <div className="mt-6">
      <div className="mb-2 flex items-center justify-between">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-text-2">{t('Reviews')}</h2>
        {user && <Button size="sm" variant="ghost" onClick={() => setWriting(true)}><Star /> {t('Write a review')}</Button>}
      </div>
      {count ? (
        <>
          <p className="mb-3 flex items-center gap-2 text-sm"><span className="text-2xl font-semibold">{average?.toFixed(1)}</span><Stars value={average ?? 0} /> <span className="text-text-2">{t('{n} reviews', { n: count })}</span></p>
          {photos.length > 0 && (
            <div className="no-scrollbar -mx-4 mb-3 flex gap-2 overflow-x-auto px-4">
              {photos.slice(0, 12).map((u) => <a key={u} href={u} target="_blank" rel="noreferrer"><img src={u} alt={t('Photo by a driver')} loading="lazy" className="size-24 shrink-0 rounded-lg object-cover" /></a>)}
            </div>
          )}
          <Card className="divide-y divide-border">{list.slice(0, 10).map((r) => <ReviewRow key={r.id} r={r} />)}</Card>
        </>
      ) : (
        <Card className="p-4 text-sm text-text-2">{t('No reviews yet. Charged here? Tell other drivers how it went.')}</Card>
      )}
      {writing && <ReviewDialog stationId={station.id} stationName={station.name} onClose={() => setWriting(false)} />}
    </div>
  )
}

function ReviewRow({ r }: { r: Review }) {
  const { t } = useI18n()
  return (
    <div className="p-4 text-sm">
      <div className="flex items-center justify-between gap-2"><Stars value={r.rating} /><span className="text-xs text-text-2">{r.author_name} · {date(r.created_at)}</span></div>
      {r.body && <p className="mt-2 whitespace-pre-wrap">{r.body}</p>}
      {r.reply && <div className="mt-2 rounded-lg bg-surface-2 p-2.5 text-[13px]"><span className="font-medium">{t('Reply from the station')}:</span> {r.reply.body}</div>}
    </div>
  )
}

export function ReviewDialog({ stationId, stationName, sessionId, onClose }: { stationId: number; stationName: string; sessionId?: number; onClose: () => void }) {
  const { t } = useI18n()
  const [rating, setRating] = useState(0)
  const [body, setBody] = useState('')
  const [photos, setPhotos] = useState<File[]>([])
  const save = useAction(() => reviews.write(stationId, { rating, body: body.trim() || undefined, session_id: sessionId, photos }), {
    quietError: true,
    success: t('Thanks for your review'),
    invalidate: [['reviews', stationId]],
    onSuccess: onClose,
  })
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()} title={t('Rate {station}', { station: stationName })} footer={<><Button variant="outline" onClick={onClose}>{t('Cancel')}</Button><Button loading={save.isPending} disabled={!rating} onClick={() => save.mutate(undefined, { onError: (e) => toast.error(isMissing(e) ? t('Reviews are coming soon.') : errorText(e)) })}>{t('Post review')}</Button></>}>
      <div className="grid gap-4">
        <div className="flex justify-center gap-1" role="radiogroup" aria-label={t('Rating')}>
          {[1, 2, 3, 4, 5].map((i) => (
            <button key={i} role="radio" aria-checked={rating === i} aria-label={`${i}`} onClick={() => setRating(i)} className="p-1">
              <Star className={cn('size-9', i <= rating ? 'fill-brand text-brand' : 'text-border-strong')} />
            </button>
          ))}
        </div>
        <Field label={t('Your review (optional)')}>{(p) => <Textarea {...p} value={body} onChange={(e) => setBody(e.target.value)} placeholder={t('Speed, waiting time, toilets, food nearby…')} />}</Field>
        <div>
          <p className="mb-2 text-[13px] font-medium">{t('Photos of the station (optional)')}</p>
          <PhotoPicker files={photos} onChange={setPhotos} />
          <p className="mt-1 text-xs text-text-2">{t('Photos help other drivers find the chargers. Please leave out people and number plates.')}</p>
        </div>
      </div>
    </Dialog>
  )
}
